import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {DataExportRunCardComponent} from './data-export-run-card.component';
import {DataExportHttpService} from '../_services/exports/data-export-http.service';
import {FiscalJobStore} from '../_services/fiscal-jobs/fiscal-job.store';
import {FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {
  ARCHIVE_FORMAT_OPTIONS,
  defaultPeriod,
  DEFAULT_UI_ARCHIVE_FORMAT,
  exportErrorMessage,
  FiscalArchiveFormat,
  FiscalExportRegister,
  registerLabel
} from '../_models/exports/data-export.models';
import {CompanyTimeZoneService} from '../../../_shared/time/company-time-zone.service';
import {companyToday} from '../../../_shared/time/company-day';

/**
 * DSFinV-K-Datenexport (#770) — where an operator, or their tax adviser, produces the machine-readable record
 * of a period's cash-register data and downloads it.
 *
 * ### What this screen exists to make unnecessary
 *
 * Before it, an export was a support ticket: somebody at CheqMe fetched the file from a third party's web site
 * and sent it on. Feature #241's whole point is that the person who needs the file produces it themselves —
 * which only holds if nothing on the way tells them whose web site it would have been. So no label, no empty
 * state, no error sentence and **no downloaded file's name** on this screen names a provider. The export is
 * described by what it is and what period it covers.
 *
 * ### Creating is asynchronous, and the screen never asks the operator to wait
 *
 * A run is scheduled on the fiscal job engine (#768) and its progress is pushed over
 * `/hubs/fiscal-jobs`; {@link FiscalJobStore} answers a push by refetching. A reload, a second tab and a visit
 * next week all show the same runs, because the state is a queryable record rather than something this screen
 * holds. That is also what makes the list below a list of **files you can fetch again**, not a log of things
 * that once happened.
 *
 * ### A past export is re-downloaded, never rebuilt
 *
 * The download endpoint reads the result reference the run's entry already holds. Pressing „Herunterladen" on
 * a January export next December fetches the January file — it does not produce a new one that might not
 * match.
 *
 * ### Why the registers are a filter and not a switch
 *
 * The far end narrows an export to at most one fiscal id, so choosing three registers produces at least three
 * entries in one run, each with its own state, its own error and its own retry. Choosing none produces a
 * single entry covering the whole location, which is what an auditor usually asks for — hence the default.
 *
 * ### One register can produce several files, and that is the register being covered COMPLETELY
 *
 * A register gets a new fiscal identity whenever its security module is replaced. The records it filed under
 * the previous one are a separate export, so a register that changed mid-period yields two entries — labelled
 * „… (Teil 1 von 2)" and „… (Teil 2 von 2)" — and the two together are the whole register. Before #770's fix
 * only one of them existed, and nothing on the screen said the other was missing.
 *
 * ### The archive format is a real choice now (#823)
 *
 * fiskaly's own export endpoint builds the archive in whichever container is asked for, so the „Archivformat"
 * field is a live `cqme-select` rather than a disabled one with an explanatory sentence. **ZIP is
 * preselected** — it is what opened directly in the validation tooling a live run tried, with no conversion
 * step — and TAR stays one click away. A run created before the choice existed, or with no `format` this
 * build can read, is still treated as TAR on screen (see {@link DEFAULT_ARCHIVE_FORMAT}); that default is
 * deliberately independent of {@link DEFAULT_UI_ARCHIVE_FORMAT}, which only ever governs a NEW export.
 *
 * ### It stops when it leaves
 *
 * {@link FiscalJobStore} is `providedIn: 'root'`, so it outlives this screen; without an `ngOnDestroy` the
 * open run and its 15-second fallback poll went on requesting a run nobody could see, for the lifetime of the
 * tab (Bug #783). {@link ngOnDestroy} releases the store, which is paired with the {@link ngOnInit} `start()`.
 */
@Component({
  selector: 'legal-data-exports',
  standalone: true,
  imports: [FormsModule, SelectComponent, DataExportRunCardComponent],
  templateUrl: './data-exports.component.html'
})
export class DataExportsComponent implements OnInit, OnDestroy {
  private readonly http = inject(DataExportHttpService);
  private readonly store = inject(FiscalJobStore);
  private readonly companyTimeZone = inject(CompanyTimeZoneService);

  /**
   * The period the form opens on: the previous full calendar month, on the COMPANY's calendar (#813).
   *
   * Read once, so the two fields cannot straddle midnight, and passed the zone the service already answers —
   * the company's own once it has loaded, the server default before that, never `undefined`.
   */
  private readonly initialPeriod = defaultPeriod(this.companyTimeZone.timeZone());

  /** The first business day of the period, `yyyy-MM-dd`. */
  protected readonly from = signal(this.initialPeriod.from);

  /** The last business day of the period, inclusive, `yyyy-MM-dd`. */
  protected readonly to = signal(this.initialPeriod.to);

  /**
   * Today in the COMPANY's calendar, so neither field can be pointed at a day that has not happened (#813).
   *
   * Derived rather than set, and never absent. {@link CompanyTimeZoneService.timeZone} answers the server's
   * own default zone until the company's has arrived, so there is a real day here from the first render, and
   * it narrows itself the moment the answer lands. A version that started at `undefined` did not merely show
   * a stale bound — Angular writes `max="undefined"`, which is not a date, so the field had NO upper bound
   * and 31.12.2099 was selectable. Neither `FiscalExportService` nor `FiscalTseExportService` refuses a
   * period that has not happened, so this is the only place that bound exists.
   */
  protected readonly today = computed(() => companyToday(this.companyTimeZone.timeZone()));

  /** The registers of this location, once they have been read. */
  protected readonly registers = signal<FiscalExportRegister[]>([]);

  /** The registers to cover, or empty for every register. */
  protected readonly selectedRegisterIds = signal<string[]>([]);

  /**
   * The archive container the NEXT run is created for (#823).
   *
   * ZIP by default ({@link DEFAULT_UI_ARCHIVE_FORMAT}) — a picker, not a constant, because fiskaly's own
   * export endpoint now builds the archive in whichever container is asked for.
   */
  protected readonly format = signal<FiscalArchiveFormat>(DEFAULT_UI_ARCHIVE_FORMAT);

  /** The containers the picker lists, the preselected one first. */
  protected readonly formatOptions = [...ARCHIVE_FORMAT_OPTIONS];

  /** True while a run is being created. */
  protected readonly creating = signal(false);

  /** The entry whose file is being collected, or null. */
  protected readonly downloadingItemId = signal<string | null>(null);

  /** The entry being produced again, or null. */
  protected readonly retryingItemId = signal<string | null>(null);

  /** The run whose entries are open, or null. */
  protected readonly openRunId = signal<string | null>(null);

  /** What went wrong, in German, or null. Cleared by the next action. */
  protected readonly message = signal<string | null>(null);

  /** True when the last action succeeded and the message is good news rather than a fault. */
  protected readonly messageIsSuccess = signal(false);

  /** The engine's list, narrowed to data exports — a closing correction (#771) is a different screen. */
  protected readonly runs = computed<FiscalJobSummary[]>(() =>
    this.store.jobs().filter(job => job.kind === 'dataExport'));

  /** The opened run with its entries, or null. */
  protected readonly openRun = computed(() => {
    const open = this.store.openJob();
    return open && open.job.id === this.openRunId() ? open : null;
  });

  /** True while the list is being read. */
  protected readonly loading = this.store.listLoading;

  /** Whether the progress connection is up. Purely informational — every path works without it. */
  protected readonly live = this.store.live;

  /**
   * The registers that can actually be covered, as the picker's own options.
   *
   * A register that was never set up for fiscal record keeping has nothing to export, and the server refuses
   * it — so it is not offered. It is still accounted for underneath, by {@link unregisteredNames} and
   * {@link unnamedUnregisteredNote}: an option that vanishes takes its own explanation with it, and "why is
   * my second till missing from this list" is the question that would otherwise reach support.
   *
   * The label is resolved here rather than in the template because a register with no name of its own arrives
   * as `name: null`, and `cqme-select` renders `labelKey` verbatim — a blank option, or worse an id, is
   * exactly what the live-screen defect was (#770).
   */
  protected readonly selectableRegisters = computed(() => this.registers()
    .filter(r => r.registered)
    .map(r => ({id: r.id, name: registerLabel(r)})));

  /**
   * The unregistered registers an operator can actually identify, as one comma-separated list.
   *
   * Only the ones with a name of their own. A register nobody named cannot be identified from this sentence,
   * so naming it here would be thirteen indistinguishable entries where twelve of them teach the reader
   * nothing — see {@link unnamedUnregisteredNote}, which counts them instead.
   */
  protected readonly unregisteredNames = computed(() => this.registers()
    .filter(r => !r.registered && r.name !== null)
    .map(r => r.name)
    .join(', '));

  /**
   * The sentence for the unregistered registers that carry no name, or `null` when there are none.
   *
   * The list above exists so a reader can tell WHICH register is missing from the picker. An unnamed one
   * cannot be told apart by any wording, and printing its id instead — which is what shipped — put internal
   * identifiers in front of a customer and their tax adviser while still answering nobody's question. So they
   * are counted, and the reader is told the one thing that helps: name them.
   */
  protected readonly unnamedUnregisteredNote = computed<string | null>(() => {
    const count = this.registers().filter(r => !r.registered && r.name === null).length;
    if (count === 0) return null;

    // "weitere … ebenfalls" only when the sentence above it actually named some: standing on its own it would
    // point back at a list that is not there.
    const named = this.unregisteredNames().length > 0;
    const subject = named
      ? (count === 1
        ? 'Eine weitere Kasse ohne Namen ist ebenfalls nicht eingerichtet.'
        : `${count} weitere Kassen ohne Namen sind ebenfalls nicht eingerichtet.`)
      : (count === 1
        ? 'Eine Kasse ohne Namen ist nicht für die Kassenführung eingerichtet.'
        : `${count} Kassen ohne Namen sind nicht für die Kassenführung eingerichtet.`);

    return `${subject} Vergeben Sie unter „Geräte“ einen Namen, damit Sie sie hier wiedererkennen.`;
  });

  /**
   * Whether both fields hold something the browser accepted.
   *
   * A native `<input type="date">` reports an empty value both for an untouched field and for a date it could
   * not parse — typing an impossible calendar day such as 31.09.2026 clears the field the same way leaving it
   * blank does. So this is also false on that kind of typo, and the template tells the two cases apart from
   * {@link periodOrderValid} rather than blaming the day order for a day that does not exist.
   */
  protected readonly periodComplete = computed(() => !!this.from() && !!this.to());

  /** Whether the complete period has „Von" on or before „Bis". */
  protected readonly periodOrderValid = computed(() => this.periodComplete() && this.from() <= this.to());

  /** Whether the create button may be pressed. */
  protected readonly canCreate = computed(() => !this.creating() && this.periodOrderValid());

  async ngOnInit(): Promise<void> {
    // The store owns the hub. Starting it here — rather than in a constructor — keeps a screen nobody opened
    // from holding a socket, which is the arrangement GateRefreshService documents.
    this.store.start();

    // Asks for the company's own zone; `today` reads it and narrows the date bound (#813). At the day's edge
    // — 00:15 Berlin while a UTC-built "today" still reads yesterday — the wrong bound makes the current day
    // unreachable, which is exactly when an operator closing out for the night needs it. `load()` replays a
    // cached zone instantly on every call after the first, so this never blocks the two reads below.
    this.companyTimeZone.load().subscribe();

    await Promise.all([this.store.refreshList('dataExport'), this.loadRegisters()]);
  }

  /**
   * Releases the store when the screen goes away (Bug #783).
   *
   * Paired with {@link ngOnInit}'s `start()`. The store is root-provided, so its own `ngOnDestroy` runs only
   * when the application is torn down — without this the open run's 15-second fallback poll went on
   * requesting `/fiscal/jobs/Get` for a run the operator had navigated away from, for the lifetime of the
   * tab. The store counts its callers, so leaving one of two screens does not take the hub down under the
   * other.
   */
  ngOnDestroy(): void {
    this.store.stop();
  }

  /** Reads the registers this location has. A failure leaves the filter empty rather than the screen broken. */
  protected async loadRegisters(): Promise<void> {
    try {
      this.registers.set(await firstValueFrom(this.http.registers()));
    } catch (error) {
      this.registers.set([]);
      this.fail(error);
    }
  }

  /** Starts a run for the chosen period and registers, and opens it. */
  protected async create(): Promise<void> {
    if (!this.canCreate()) return;

    this.creating.set(true);
    this.message.set(null);

    try {
      const created = await firstValueFrom(this.http.create({
        from: this.from(),
        to: this.to(),
        // Omitted rather than sent empty: "no selection" means every register, and an empty array would read
        // as "these zero registers" to anything that ever inspects the request.
        registerIds: this.selectedRegisterIds().length > 0 ? this.selectedRegisterIds() : undefined,
        format: this.format()
      }));

      // Read it back through the store, so the list row and the opened run come from ONE read path and cannot
      // disagree — and so the hub's pushes land on a run the store already knows.
      this.openRunId.set(created.job.id);
      await this.store.refreshList('dataExport');
      await this.store.open(created.job.id);

      this.succeed('Der Export wurde angefordert. Sobald er bereitsteht, können Sie ihn hier herunterladen.');
    } catch (error) {
      this.fail(error);
    } finally {
      this.creating.set(false);
    }
  }

  /** Opens or closes a run. */
  protected async toggle(jobId: string): Promise<void> {
    if (this.openRunId() === jobId) {
      this.openRunId.set(null);
      this.store.close();
      return;
    }

    this.openRunId.set(jobId);
    await this.store.open(jobId);
  }

  /**
   * Collects one entry's file and hands it to the browser.
   *
   * The name comes from the server's `Content-Disposition` — it is decided by one rule there, because a file
   * name is the one piece of this feature that leaves the application. Nothing is guessed from the run's
   * stored container: that value records what the run ASKED for, and the export is handed over as it was
   * produced, so a guess from it would be the #770 lie again in the one place nobody can check it.
   */
  protected async downloadItem(item: FiscalJobItem): Promise<void> {
    if (this.downloadingItemId()) return;

    this.downloadingItemId.set(item.id);
    this.message.set(null);

    try {
      const file = await firstValueFrom(this.http.download(this.openRunId()!, item.id));
      this.save(file.blob, file.fileName);
    } catch (error) {
      // The server has just recorded that the artefact is gone, so the entry is no longer „fertig" — re-read
      // it, which is what swaps „Herunterladen" for „Erneut erstellen" without a reload (#770 review D4).
      // Every other refusal says nothing about the entry and leaves the screen as it is.
      if (this.fail(error) === 'E_FISCAL_EXPORT_DOWNLOAD_GONE') {
        const jobId = this.openRunId();
        if (jobId) await this.store.open(jobId);
      }
    } finally {
      this.downloadingItemId.set(null);
    }
  }

  /**
   * Produces one entry again, on its own.
   *
   * Never the whole run: the entries that already succeeded have files an auditor may already hold, and
   * re-running them would replace those files with new ones for no reason.
   */
  protected async retryItem(item: FiscalJobItem): Promise<void> {
    const jobId = this.openRunId();
    if (!jobId || this.retryingItemId()) return;

    this.retryingItemId.set(item.id);
    this.message.set(null);

    const accepted = await this.store.retryItem(jobId, item.id);
    this.retryingItemId.set(null);

    if (accepted) {
      this.succeed('Der Eintrag wird erneut erstellt.');
      return;
    }

    this.message.set(exportErrorMessage(this.store.lastError()));
    this.messageIsSuccess.set(false);
  }

  /** The options the register picker renders. */
  protected onRegistersChanged(value: string | string[] | undefined): void {
    // cqme-select declares its output as `TValue | TValue[] | undefined` even in multiple mode, because one
    // component serves both. Normalised once here rather than cast at the template.
    if (Array.isArray(value)) this.selectedRegisterIds.set(value);
    else if (typeof value === 'string') this.selectedRegisterIds.set([value]);
    else this.selectedRegisterIds.set([]);
  }

  /** The archive-format picker's own choice (#823). Single mode, so `cqme-select` never emits an array. */
  protected onFormatChanged(value: FiscalArchiveFormat | FiscalArchiveFormat[] | undefined): void {
    if (value === 'tar' || value === 'zip') this.format.set(value);
  }

  /** Saves a blob under a name, the way every other download in this app does. */
  private save(blob: Blob, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(blob);
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  /**
   * Records a refusal, translated into German by its code, and answers the code it found.
   *
   * The code is returned rather than only rendered because one refusal needs an action beyond a sentence —
   * see {@link downloadItem}. It stays `null` for an error with no catalogued code, which is exactly when
   * nothing but the fallback sentence is safe to do.
   */
  private fail(error: unknown): string | null {
    const body = (error as { error?: { code?: unknown } } | null)?.error;
    const code = typeof body?.code === 'string' ? body.code : null;
    this.message.set(exportErrorMessage(code));
    this.messageIsSuccess.set(false);
    return code;
  }

  /** Records a success sentence. */
  private succeed(text: string): void {
    this.message.set(text);
    this.messageIsSuccess.set(true);
  }
}
